import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalInstance, copilotTokenRequest } from '../config/msalConfig';

// Microsoft 365 Copilot Chat API (beta) - delegated access only, requires a Copilot license per user
const COPILOT_BASE_URL = 'https://graph.microsoft.com/beta/copilot/conversations';

interface CopilotMessage {
  id: string;
  text: string;
  createdDateTime: string;
}

interface CopilotConversation {
  id: string;
  messages?: CopilotMessage[];
}

/** Thrown when a background (non-interactive) request needs the user to consent/sign in first. */
export class CopilotInteractionRequiredError extends Error {}

class CopilotService {
  private async getToken(interactive: boolean): Promise<string> {
    const account = msalInstance.getAllAccounts()[0];
    if (!account) {
      throw new Error('Ingen inloggad användare');
    }

    try {
      const response = await msalInstance.acquireTokenSilent({ ...copilotTokenRequest, account });
      return response.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        if (!interactive) {
          throw new CopilotInteractionRequiredError('Copilot kräver inloggning/godkännande');
        }
        const response = await msalInstance.acquireTokenPopup({ ...copilotTokenRequest, account });
        return response.accessToken;
      }
      throw err;
    }
  }

  private async post<T>(url: string, token: string, body: unknown): Promise<T> {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Copilot svarade ${response.status}: ${errorText}`);
    }
    return response.json();
  }

  /**
   * Sends a single prompt in a new conversation and returns Copilot's answer as markdown text.
   * The prompt may be a promise so it can be built while the conversation is being created.
   * With interactive = false no popup is shown; CopilotInteractionRequiredError is thrown instead.
   */
  async ask(prompt: string | Promise<string>, interactive = true): Promise<string> {
    const startedAt = performance.now();
    const elapsed = () => `${Math.round(performance.now() - startedAt)} ms`;

    const token = await this.getToken(interactive);
    console.log(`[Copilot] Token: ${elapsed()}`);

    const [conversation, text] = await Promise.all([
      this.post<CopilotConversation>(COPILOT_BASE_URL, token, {}).then((c) => {
        console.log(`[Copilot] Conversation created: ${elapsed()}`);
        return c;
      }),
      Promise.resolve(prompt).then((p) => {
        console.log(`[Copilot] Prompt ready (${p.length} chars): ${elapsed()}`);
        return p;
      }),
    ]);

    const chatStartedAt = performance.now();
    const result = await this.post<CopilotConversation>(
      `${COPILOT_BASE_URL}/${conversation.id}/chat`,
      token,
      {
        message: { text },
        locationHint: { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone },
        // Only ground on the user's work data, not the web
        contextualResources: { webContext: { isWebEnabled: false } },
      }
    );

    // The last message in the conversation is Copilot's reply; the one before it echoes the prompt
    const messages = result.messages ?? [];
    const answer = messages[messages.length - 1];
    if (!answer || messages.length < 2) {
      throw new Error('Copilot returnerade inget svar');
    }
    console.log(
      `[Copilot] Chat answered in ${Math.round(performance.now() - chatStartedAt)} ms ` +
      `(${answer.text.length} chars). Total: ${elapsed()}`
    );
    return answer.text;
  }
}

export const copilotService = new CopilotService();
